import { theme } from '@buggy/shared'
import type { ApexOptions } from 'apexcharts'
import { mean } from 'lodash-es'
import Image from 'next/image'
import { FunctionComponent, useEffect, useMemo } from 'react'
import styled from 'styled-components'
import type { OurIntlShape } from '@/i18n/typing'
import PlatformImage from '@/utils/export-pptx/components/platform-image'
import {
  Card,
  CardTitle,
  SlideContainer,
  SlideTitle,
} from '@/utils/export-pptx/components/slides/styles'
import { chartSize, PptxObjectType } from '@/utils/export-pptx/constants'
import useApexChartDataUri from '@/utils/export-pptx/hooks/use-apex-chart-data-uri'
import type { KolCustomizedTagFollowerLevelSlide } from '@/utils/export-pptx/types/slide'

interface SlideProps {
  slide: KolCustomizedTagFollowerLevelSlide
  onSlideReady?: (slide: KolCustomizedTagFollowerLevelSlide) => void
  formatNumber: OurIntlShape['formatNumber']
  formatMessage: OurIntlShape['formatMessage']
}

const SlideComponent: FunctionComponent<SlideProps> = ({
  slide,
  onSlideReady,
  formatNumber,
  formatMessage,
}) => {
  const customizedTagOptions: ApexOptions | undefined = useMemo(() => {
    if (!slide.payload.customizedTagRanking) {
      return undefined
    }

    const annotations = slide.payload.showAverage
      ? {
          yaxis: [
            {
              y: mean(slide.payload.customizedTagRanking.data),
              strokeDashArray: 0,
              borderColor: theme.colors.text.secondary,
              label: {
                text:
                  `${formatMessage({ id: 'general:project_average' })} ${formatNumber(
                    mean(slide.payload.customizedTagRanking.data),
                    { maximumFractionDigits: 2, minimumFractionDigits: 2 },
                  )}` + (slide.payload.isPercentage ? '%' : ''),
                borderColor: theme.colors.text.secondary,
                offsetY: 6,
                style: {
                  color: theme.colors.text.white,
                  background: theme.colors.text.secondary,
                  padding: { top: 4, bottom: 4, left: 6, right: 6 },
                },
              },
            },
          ],
        }
      : undefined

    return {
      series: [{ data: slide.payload.customizedTagRanking.data }],
      chart: { type: 'bar', width: chartSize.bar.w, height: chartSize.bar.h },
      dataLabels: {
        formatter: (value: number): string =>
          formatNumber(value, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
          }) + (slide.payload.isPercentage ? '%' : ''),
      },
      xaxis: { categories: slide.payload.customizedTagRanking.categories },
      yaxis: {
        labels: { formatter: (value) => String(Math.round(value)) },
        title: { text: slide.payload.isPercentage ? '% (Percentage)' : '' },
      },
      annotations,
    }
  }, [
    formatMessage,
    formatNumber,
    slide.payload.customizedTagRanking,
    slide.payload.isPercentage,
    slide.payload.showAverage,
  ])

  const customizedTagDataUri = useApexChartDataUri(customizedTagOptions!, {
    skip: customizedTagOptions === undefined,
  })

  const isCustomizedTagChartReady =
    customizedTagOptions === undefined || customizedTagDataUri !== undefined

  const followerLevelOptions: ApexOptions | undefined = useMemo(() => {
    if (!slide.payload.followerLevelRanking) {
      return undefined
    }

    const annotations = slide.payload.showAverage
      ? {
          yaxis: [
            {
              y: mean(slide.payload.followerLevelRanking.data),
              strokeDashArray: 0,
              borderColor: theme.colors.text.secondary,
              label: {
                text:
                  `${formatMessage({ id: 'general:project_average' })} ${formatNumber(
                    mean(slide.payload.followerLevelRanking.data),
                    { minimumFractionDigits: 0, maximumFractionDigits: 2 },
                  )}` + (slide.payload.isPercentage ? '%' : ''),
                borderColor: theme.colors.text.secondary,
                offsetY: 6,
                style: {
                  color: theme.colors.text.white,
                  background: theme.colors.text.secondary,
                  padding: { top: 4, bottom: 4, left: 6, right: 6 },
                },
              },
            },
          ],
        }
      : undefined

    return {
      series: [{ data: slide.payload.followerLevelRanking.data }],
      chart: { type: 'bar', width: chartSize.bar.w, height: chartSize.bar.h },
      dataLabels: {
        formatter: (value: number): string =>
          formatNumber(value, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
          }) + (slide.payload.isPercentage ? '%' : ''),
      },
      xaxis: { categories: slide.payload.followerLevelRanking.categories },
      yaxis: {
        labels: { formatter: (value) => String(Math.round(value)) },
        title: { text: slide.payload.isPercentage ? '% (Percentage)' : '' },
      },
      annotations,
    }
  }, [
    formatMessage,
    formatNumber,
    slide.payload.followerLevelRanking,
    slide.payload.isPercentage,
    slide.payload.showAverage,
  ])

  const followerLevelDataUri = useApexChartDataUri(followerLevelOptions!, {
    skip: followerLevelOptions === undefined,
  })

  const isFollowerLevelChartReady =
    followerLevelOptions === undefined || followerLevelDataUri !== undefined

  const isChartReady = isCustomizedTagChartReady && isFollowerLevelChartReady

  useEffect(() => {
    if (!isChartReady) {
      return
    }

    onSlideReady?.(slide)
  }, [onSlideReady, slide, isChartReady])

  return (
    <SlideContainer data-pptx-object={PptxObjectType.Shape}>
      <SlideTitle data-pptx-object={PptxObjectType.Shape}>
        <PlatformImage
          height={54}
          platform={slide.payload.platform}
          width={54}
        />

        <span data-pptx-object={PptxObjectType.Text}>
          {formatMessage({
            id: 'insight_report:slide_kol_performance_analysis',
          })}
        </span>
      </SlideTitle>

      <GridContent>
        {customizedTagDataUri && (
          <Card data-pptx-object={PptxObjectType.Shape}>
            <CardTitle data-pptx-object={PptxObjectType.Text}>
              {slide.payload.customizedTagRanking?.title}
            </CardTitle>

            <Image
              data-original-size
              alt='kol-type-interaction-rank-chart'
              data-pptx-object={PptxObjectType.Image}
              height={chartSize.bar.h}
              src={customizedTagDataUri}
              width={chartSize.bar.w}
            />
          </Card>
        )}

        {followerLevelDataUri && (
          <Card data-pptx-object={PptxObjectType.Shape}>
            <CardTitle data-pptx-object={PptxObjectType.Text}>
              {slide.payload.followerLevelRanking?.title}
            </CardTitle>

            <Image
              data-original-size
              alt='follower-count-range-rank-chart'
              data-pptx-object={PptxObjectType.Image}
              height={chartSize.bar.h}
              src={followerLevelDataUri}
              width={chartSize.bar.w}
            />
          </Card>
        )}
      </GridContent>
    </SlideContainer>
  )
}

const GridContent = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 32px;

  width: 100%;
`

export default SlideComponent
